import { useCallback, useEffect, useRef, useState } from 'react';
import type { LeadProposalGenerationType } from '@econtractor/types';
import * as apiProposal from 'api/apiProposal';

const useLeadProposalGenerations = (leadId?: number | string) => {
    const [rows, setRows] = useState<LeadProposalGenerationType[]>([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [togglingId, setTogglingId] = useState<number | null>(null);
    const loadedLeadIdRef = useRef<number>(0);

    const refresh = useCallback(async () => {
        const targetLeadId = Number(leadId || 0);
        if (!targetLeadId) {
            setRows([]);
            loadedLeadIdRef.current = 0;
            setLoading(false);
            setRefreshing(false);
            return;
        }

        const isInitial = loadedLeadIdRef.current !== targetLeadId;
        if (isInitial) setLoading(true);
        else setRefreshing(true);

        const result = await apiProposal.getLeadProposalGenerations(targetLeadId);
        setRows(result || []);
        loadedLeadIdRef.current = targetLeadId;
        setLoading(false);
        setRefreshing(false);
    }, [leadId]);

    const toggleAccept = useCallback(
        async (generationId: number, nextAccept: boolean): Promise<boolean> => {
            const targetLeadId = Number(leadId || 0);
            if (!targetLeadId || !generationId) return false;

            const deactivateOthers = nextAccept;
            const prevRows = rows;

            setTogglingId(generationId);
            setRows((prev) =>
                prev.map((row) => {
                    if (row.ID === generationId) {
                        return { ...row, proposavAccept: nextAccept ? 1 : 0 };
                    }
                    if (deactivateOthers) {
                        return { ...row, proposavAccept: 0 };
                    }
                    return row;
                }),
            );

            const stat = await apiProposal.setLeadProposalGenerationAccept(
                targetLeadId,
                generationId,
                nextAccept,
                deactivateOthers ? { deactivateOthers: true } : undefined,
            );

            if (!stat) {
                setRows(prevRows);
            }

            setTogglingId(null);
            return Boolean(stat);
        },
        [leadId, rows],
    );

    useEffect(() => {
        void refresh();
    }, [refresh]);

    return {
        rows,
        loading,
        refreshing,
        togglingId,
        refresh,
        toggleAccept,
    };
};

export default useLeadProposalGenerations;
